import React from "react"
import DownloadAppModal from '../app_components/DownloadAppModal'
import Icon from '../app_components/Icon'
import { openAppStoreForDevice } from '../../utils/appStoreLinks'
import { BobbingEmoji, Spinner, entranceStyle, gradient, hexToRgba } from '../games_components/GameUI'
import {
  activityEmoji,
  gameGradient,
  isInteractiveGame,
  levelTags,
  tilePalette,
} from '../games_components/activityTheme'

const FLOATERS = [
  { emoji: '🎲', size: 34, top: 14, right: 18, tilt: '12deg', delay: 0 },
  { emoji: '⭐', size: 22, top: 66, right: 78, tilt: '-10deg', delay: 400 },
  { emoji: '🎉', size: 30, top: 92, right: 14, tilt: '-14deg', delay: 800 },
  { emoji: '💬', size: 24, top: 4, right: 96, tilt: '8deg', delay: 1200 },
]

// Roughly what fits in the two-line clamped preview.
const PREVIEW_CHARS = 100

function parseJson(value, fallback) {
  if (typeof value !== 'string') return value || fallback
  try {
    return JSON.parse(value)
  } catch (error) {
    return fallback
  }
}

function bodyLines(text) {
  return (text || '').split(/\r?\n|\r/).filter((line) => line.trim() !== '')
}

const StatPill = ({ emoji, label }) => (
  <span className="actvHero__stat">
    <span aria-hidden="true">{emoji}</span>
    {label}
  </span>
)

const SectionTitle = ({ emoji, title, subtitle }) => (
  <div className="actvSection__title">
    <h2 className="actvSection__heading">
      <span aria-hidden="true">{emoji}</span>
      {title}
    </h2>
    <p className="actvSection__subtitle">{subtitle}</p>
  </div>
)

const GameCard = ({ game, index, entranceIndex }) => {
  const colors = gameGradient(index)
  return (
    <a
      className="actvGame gpEnter"
      href={`/games?pack=${game.id}`}
      aria-label={`Play ${game.title}`}
      style={{
        ...entranceStyle(entranceIndex),
        background: gradient(colors),
        boxShadow: `0 12px 24px ${hexToRgba(colors[0], 0.3)}`,
      }}
    >
      <span className="actvGame__sparkle" aria-hidden="true" />
      <span className="actvGame__row">
        <BobbingEmoji
          emoji={activityEmoji(game)}
          image={game.teaser_gif_file_name ? game.teaser_gif_url : null}
          size={64}
          delay={index * 300}
          className="actvGame__emoji"
        />
        <span className="actvGame__text">
          <span className="actvGame__live">
            <span className="actvGame__liveDot" aria-hidden="true" />
            LIVE GAME
          </span>
          <span className="actvGame__title">{game.title}</span>
        </span>
      </span>
      {!!game.body && <span className="actvGame__body">{game.body}</span>}
      <span className="actvGame__footer">
        <span className="actvGame__meta">
          <span className="actvGame__metaPill"><span aria-hidden="true">👥</span> 2 players</span>
          <span className="actvGame__metaPill"><span aria-hidden="true">⚡</span> Real-time</span>
        </span>
        <span className="actvGame__play" style={{ color: colors[0] }}>
          Play <Icon name="arrow-right" solid={false} size={18} color={colors[0]} />
        </span>
      </span>
    </a>
  )
}

const ConversationCard = ({ activity, index, entranceIndex, expanded, onToggle, onDownloadApp }) => {
  const palette = tilePalette(index)
  const levels = levelTags(activity)
  const lines = bodyLines(activity.body)
  const detailsId = `actv-details-${activity.id}`
  const expandable = lines.length > 1 ||
    (activity.body || '').length > PREVIEW_CHARS ||
    !!activity.focus ||
    activity.app_only === true
  const Header = expandable ? 'button' : 'div'
  const headerProps = expandable
    ? { type: 'button', onClick: onToggle, 'aria-expanded': expanded, 'aria-controls': detailsId }
    : {}

  return (
    <article
      className={`actvCard gpEnter${expanded ? ' actvCard--open' : ''}`}
      style={entranceStyle(entranceIndex)}
    >
      <Header
        className={`actvCard__toggle${expandable ? '' : ' actvCard__toggle--static'}`}
        {...headerProps}
      >
        <span
          className="actvCard__tile"
          aria-hidden="true"
          style={{
            background: gradient(palette.tile),
            transform: `rotate(${index % 2 === 0 ? '-6deg' : '6deg'})`,
          }}
        >
          {activityEmoji(activity)}
        </span>
        <span className="actvCard__text">
          <span className="actvCard__title">{activity.title}</span>
          {lines.length > 0 && !(expanded && lines.length > 1) && (
            <span className="actvCard__preview">{lines.join(' ')}</span>
          )}
        </span>
        {expandable && (
          <span className="actvCard__chevron" aria-hidden="true" style={{ background: palette.soft }}>
            <Icon name="angle-down" solid={false} size={20} color={palette.accent} />
          </span>
        )}
      </Header>

      {levels.length > 0 && (
        <div className="actvCard__chips">
          {levels.map((tag) => (
            <span key={tag.id} className={`actvChip actvChip--level${tag.id}`}>
              <span className="actvChip__dot" aria-hidden="true" />
              {tag.name}
            </span>
          ))}
        </div>
      )}

      {expandable && expanded && (
        <div className="actvCard__details" id={detailsId}>
          {lines.length > 1 && (
            <div className="actvCard__body">
              {lines.map((line, i) => <p key={i}>{line}</p>)}
            </div>
          )}
          {!!activity.focus && (
            <div className="actvCard__focus" style={{ background: palette.soft }}>
              <p className="actvCard__focusLabel" style={{ color: palette.accent }}>✨ Vocabulary focus</p>
              <p className="actvCard__focusText">{activity.focus}</p>
            </div>
          )}
          {activity.app_only === true && (
            <div className="actvCard__appOnly">
              <span className="actvCard__appOnlyText">📱 This one lives in the Lexody mobile app.</span>
              <button type="button" className="gpButton gpButton--soft gpButton--small" onClick={onDownloadApp}>
                Get the app
              </button>
            </div>
          )}
        </div>
      )}
    </article>
  )
}

class ActivitiesPage extends React.Component {
  constructor(props) {
    super(props)

    this.state = {
      games: [],
      conversations: [],
      expandedIds: [],
      loaded: false,
      showDownloadModal: false,
    }
  }

  componentDidMount() {
    $.ajax({
      url: '/get_all_exchanges',
      dataType: 'JSON',
      type: 'GET',
      success: (data) => {
        const activities = parseJson(data.data.exchanges, [])
        this.setState({
          games: activities.filter(isInteractiveGame),
          conversations: activities.filter((activity) => !isInteractiveGame(activity)),
          loaded: true,
        })
      },
      error: (xhr, status, err) => {
        console.log(err)
        this.setState({ loaded: true })
      },
    })
  }

  toggleActivity(id) {
    this.setState(({ expandedIds }) => ({
      expandedIds: expandedIds.includes(id)
        ? expandedIds.filter((expandedId) => expandedId !== id)
        : expandedIds.concat(id),
    }))
  }

  handleDownloadApp = () => {
    if (openAppStoreForDevice()) {
      this.setState({ showDownloadModal: true })
    }
  }

  renderHero() {
    const { games, conversations } = this.state
    return (
      <section className="actvHero gpEnter" style={entranceStyle(0)}>
        <span className="gpHero__blob gpHero__blob--large" aria-hidden="true" />
        <span className="gpHero__blob gpHero__blob--small" aria-hidden="true" />
        {FLOATERS.map((floater) => (
          <span
            key={floater.emoji}
            className="actvHero__floater"
            aria-hidden="true"
            style={{
              top: floater.top,
              right: floater.right,
              fontSize: floater.size,
              '--gp-tilt': floater.tilt,
              animationDelay: `${floater.delay}ms`,
            }}
          >
            {floater.emoji}
          </span>
        ))}

        <p className="actvHero__kicker">GAMES &amp; ACTIVITIES</p>
        <h1 className="actvHero__title">Let's play!</h1>
        <p className="actvHero__subtitle">Games and conversation starters that make every Lex a blast.</p>

        <div className="actvHero__stats">
          {games.length > 0 && (
            <StatPill emoji="🎮" label={`${games.length} live ${games.length === 1 ? 'game' : 'games'}`} />
          )}
          {conversations.length > 0 && (
            <StatPill emoji="💬" label={`${conversations.length} conversation starters`} />
          )}
        </div>

        <a className="actvHero__join" href="/games?join=1">
          <span className="actvHero__joinText">
            <span className="actvHero__joinTitle">Join a game</span>
            <span className="actvHero__joinSubtitle">Got a code from your partner?</span>
          </span>
          <span className="actvHero__joinArrow" aria-hidden="true">
            <Icon name="arrow-right" solid={false} size={20} color="#FFFFFF" />
          </span>
        </a>
      </section>
    )
  }

  renderActivities() {
    const { games, conversations, expandedIds, loaded } = this.state

    if (!loaded) {
      return <div className="gpLoading"><Spinner /></div>
    }

    if (games.length === 0 && conversations.length === 0) {
      return (
        <div className="actvEmpty">
          <span className="actvEmpty__emoji" aria-hidden="true">🎈</span>
          <p className="actvEmpty__title">New games are on the way</p>
          <p className="actvEmpty__text">Check back soon for fresh activities.</p>
        </div>
      )
    }

    const firstConversationSlot = games.length > 0 ? games.length + 1 : 1

    return (
      <>
        {games.length > 0 && (
          <section className="actvSection">
            <SectionTitle emoji="🎮" title="Live games" subtitle="Play together in real time" />
            <div className="actvGrid actvGrid--games">
              {games.map((game, index) => (
                <GameCard key={game.id} game={game} index={index} entranceIndex={index + 1} />
              ))}
            </div>
          </section>
        )}

        {conversations.length > 0 && (
          <section className="actvSection">
            <SectionTitle emoji="💬" title="Conversation starters" subtitle="Click a card to see the details" />
            <div className="actvGrid">
              {conversations.map((activity, index) => (
                <ConversationCard
                  key={activity.id}
                  activity={activity}
                  index={index}
                  entranceIndex={Math.min(firstConversationSlot + index, 8)}
                  expanded={expandedIds.includes(activity.id)}
                  onToggle={() => this.toggleActivity(activity.id)}
                  onDownloadApp={this.handleDownloadApp}
                />
              ))}
            </div>
          </section>
        )}

        <a className="actvPartner gpEnter" href="/profile/home" style={entranceStyle(4)}>
          <span className="actvPartner__emoji" aria-hidden="true">🤝</span>
          <span className="actvPartner__text">
            <span className="actvPartner__title">Better with a partner</span>
            <span className="actvPartner__subtitle">Find someone to play with and practice for real.</span>
          </span>
          <span className="actvPartner__arrow" aria-hidden="true">
            <Icon name="arrow-right" solid={false} size={20} color="#FFFFFF" />
          </span>
        </a>
      </>
    )
  }

  render() {
    return (
      <div className="gpPage actvPage">
        <div className="actvPage__inner">
          {this.renderHero()}
          {this.renderActivities()}
        </div>
        {this.state.showDownloadModal && (
          <DownloadAppModal onClose={() => this.setState({ showDownloadModal: false })} />
        )}
      </div>
    )
  }
}

export default ActivitiesPage
